/** Compact display formatting. The simulation keeps full precision. */

export function formatNumber(value: number, maxDecimals = 2): string {
  if (!Number.isFinite(value)) {
    return '—';
  }
  const rounded = Number(value.toFixed(maxDecimals));
  return rounded.toLocaleString('pt-BR', { maximumFractionDigits: maxDecimals });
}

/** Beyond this many digits the HUD shows a compact form instead. */
const COMPACT_DIGIT_THRESHOLD = 16;

/**
 * Formats an unbounded decimal string - level, XP, Gold, points.
 *
 * Never goes through `number`: those values have no cap, so `Number(value)`
 * would stop being exact long before the progression curve does. Grouping and
 * the compact form are both derived from the digits themselves.
 */
export function formatDecimal(value: string): string {
  const digits = value.replace(/^0+(?=\d)/, '');
  if (!/^\d+$/.test(digits)) {
    return '—';
  }
  if (digits.length <= COMPACT_DIGIT_THRESHOLD) {
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }
  // "1,23e42" - the exponent is the digit count, so nothing is rounded into
  // existence that the string did not already say.
  return `${digits[0]},${digits.slice(1, 3)}e${digits.length - 1}`;
}

export function formatPercent(value: number): string {
  return `${formatNumber(value, 2)}%`;
}

export function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatCountdown(seconds: number | null): string {
  if (seconds === null) {
    return 'BOSS!';
  }
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}

/**
 * VIP time left, in the coarsest honest unit.
 *
 * Days while there are days, then hours, then minutes - a countdown to the
 * second on a thirty-day subscription is noise. Never negative: the server
 * floors it at zero and this never invents a smaller number than it was given.
 */
export function formatVipRemaining(seconds: number): string {
  if (seconds <= 0) {
    return 'expirado';
  }
  const days = Math.floor(seconds / 86_400);
  if (days >= 1) {
    return `${days} ${days === 1 ? 'dia' : 'dias'}`;
  }
  const hours = Math.floor(seconds / 3600);
  if (hours >= 1) {
    return `${hours} h`;
  }
  return `${Math.max(1, Math.floor(seconds / 60))} min`;
}
